import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  ChartBarIcon,
  ServerIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CircleStackIcon,
  UsersIcon,
  WifiIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

interface PostgreSQLInstance {
  id: string;
  name: string;
  host: string;
  port: number;
  current_connections: number;
  max_connections: number;
  total_users: number;
  total_databases: number;
  status: 'healthy' | 'warning' | 'error';
}

interface KubernetesCluster {
  id: string;
  name: string;
  total_nodes: number;
  total_pods: number;
  running_pods: number;
  total_namespaces: number;
  status: 'healthy' | 'warning' | 'error';
}

interface DashboardData {
  postgresql: {
    instances: PostgreSQLInstance[];
    total_instances: number;
    total_connections: number;
    total_users: number;
    total_databases: number;
  };
  kubernetes: {
    clusters: KubernetesCluster[];
    total_clusters: number;
    total_nodes: number;
    total_pods: number;
    running_pods: number;
  };
  alerts: {
    total: number;
    unread: number;
    read: number;
    critical: number;
    error: number;
    warning: number;
    info: number;
  };
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      // Use Promise.allSettled to handle individual API failures gracefully
      const [postgresqlResult, kubernetesResult, alertsResult] = await Promise.allSettled([
        axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.DATABASE_OVERVIEW)),
        axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.KUBERNETES_CLUSTERS)),
        axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.ALERTS_STATS))
      ]);

      // Check if all APIs failed
      const allRejected = postgresqlResult.status === 'rejected' && 
                         kubernetesResult.status === 'rejected' && 
                         alertsResult.status === 'rejected';
      
      if (allRejected) {
        setError('Failed to fetch data from all services');
        setLoading(false);
        return;
      }

      try {
        // Extract data from results, with fallbacks for failed requests
        const postgresqlData = postgresqlResult.status === 'fulfilled' ? postgresqlResult.value : { data: { data: { instances: [] } } };
        const kubernetesData = kubernetesResult.status === 'fulfilled' ? kubernetesResult.value : { data: { data: { clusters: [] } } };
        const alertsStats = alertsResult.status === 'fulfilled' ? alertsResult.value : { data: { data: {} } };

        // Transform database overview data
        const postgresqlOverview = postgresqlData.data.data || {};
        const dbInstances = postgresqlOverview.instances || [];
        
        // Transform kubernetes data to match expected format
        const kubernetesClusters = kubernetesData.data.data?.clusters || [];
        
        // Fetch detailed kubernetes overview data
        let transformedKubernetesData = {
          clusters: kubernetesClusters.map((cluster: any) => ({
            id: cluster.name || cluster.kube_cluster_name,
            name: cluster.name || cluster.kube_cluster_name,
            total_nodes: 0,
            total_pods: 0,
            running_pods: 0,
            total_namespaces: 0,
            status: 'healthy' as const
          })),
          total_clusters: kubernetesClusters.length,
          total_nodes: 0,
          total_pods: 0,
          running_pods: 0
        };
        
        // Try to get detailed overview data
        try {
          const overviewResult = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.KUBERNETES_OVERVIEW));
          if (overviewResult.status === 200 && overviewResult.data.success) {
            const overviewData = overviewResult.data.data;
            transformedKubernetesData = {
              clusters: overviewData.clusters || [],
              total_clusters: overviewData.total_clusters || 0,
              total_nodes: overviewData.total_nodes || 0,
              total_pods: overviewData.total_pods || 0,
              running_pods: overviewData.running_pods || 0
            };
          }
        } catch (error) {
          console.error('Failed to fetch kubernetes overview:', error);
          // Keep the fallback data with basic cluster info
        }

        setData({
          postgresql: {
            instances: dbInstances,
            total_instances: postgresqlOverview.total_instances || 0,
            total_connections: postgresqlOverview.total_connections || 0,
            total_users: postgresqlOverview.total_users || 0,
            total_databases: postgresqlOverview.total_databases || 0
          },
          kubernetes: transformedKubernetesData,
          alerts: alertsStats.data.data || {
            total: 0,
            unread: 0,
            read: 0,
            critical: 0,
            error: 0,
            warning: 0,
            info: 0
          }
        });
      } catch (error: any) {
        console.error('Unexpected error in fetchData:', error);
        setError('Failed to process data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-danger">
        <p>{error}</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-success-600';
      case 'warning': return 'text-warning-600';
      case 'error': return 'text-danger-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon className="h-5 w-5 text-success-500" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-warning-500" />;
      case 'error': return <XCircleIcon className="h-5 w-5 text-danger-500" />;
      default: return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="mt-1 text-sm text-gray-500">
          Real-time monitoring of PostgreSQL and Kubernetes systems
        </p>
      </div>

      {/* PostgreSQL Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <ServerIcon className="h-6 w-6 text-primary-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">PostgreSQL Instances</h2>
          </div>
        </div>
        <div className="card-body">
          {data?.postgresql.instances && data.postgresql.instances.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {data.postgresql.instances.map((instance) => (
                <div key={instance.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{instance.name}</h3>
                    {getStatusIcon(instance.status)}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Host</span>
                      <span className="text-sm font-medium">{instance.host}:{instance.port}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Current Connections</span>
                      <span className="text-sm font-medium text-primary-600">
                        {instance.current_connections}/{instance.max_connections}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Total Users</span>
                      <span className="text-sm font-medium">{instance.total_users}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Total Databases</span>
                      <span className="text-sm font-medium">{instance.total_databases}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No PostgreSQL instances found
            </div>
          )}
          
          {/* PostgreSQL Summary */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="bg-primary-50 rounded-lg p-4">
              <div className="flex items-center">
                <ServerIcon className="h-5 w-5 text-primary-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-primary-600">Total Instances</p>
                  <p className="text-2xl font-bold text-primary-900">{data?.postgresql.total_instances || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-success-50 rounded-lg p-4">
              <div className="flex items-center">
                <WifiIcon className="h-5 w-5 text-success-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-success-600">Total Connections</p>
                  <p className="text-2xl font-bold text-success-900">{data?.postgresql.total_connections || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-warning-50 rounded-lg p-4">
              <div className="flex items-center">
                <UsersIcon className="h-5 w-5 text-warning-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-warning-600">Total Users</p>
                  <p className="text-2xl font-bold text-warning-900">{data?.postgresql.total_users || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-info-50 rounded-lg p-4">
              <div className="flex items-center">
                <CircleStackIcon className="h-5 w-5 text-info-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-info-600">Total Databases</p>
                  <p className="text-2xl font-bold text-info-900">{data?.postgresql.total_databases || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kubernetes Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <CubeIcon className="h-6 w-6 text-warning-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Kubernetes Clusters</h2>
          </div>
        </div>
        <div className="card-body">
          {data?.kubernetes.clusters && data.kubernetes.clusters.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {data.kubernetes.clusters.map((cluster) => (
                <div key={cluster.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{cluster.name}</h3>
                    {getStatusIcon(cluster.status)}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Nodes</span>
                      <span className="text-sm font-medium">{cluster.total_nodes}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Running Pods</span>
                      <span className="text-sm font-medium text-success-600">
                        {cluster.running_pods}/{cluster.total_pods}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Namespaces</span>
                      <span className="text-sm font-medium">{cluster.total_namespaces}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No Kubernetes clusters found
            </div>
          )}
          
          {/* Kubernetes Summary */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="bg-warning-50 rounded-lg p-4">
              <div className="flex items-center">
                <CubeIcon className="h-5 w-5 text-warning-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-warning-600">Total Clusters</p>
                  <p className="text-2xl font-bold text-warning-900">{data?.kubernetes.total_clusters || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-info-50 rounded-lg p-4">
              <div className="flex items-center">
                <ServerIcon className="h-5 w-5 text-info-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-info-600">Total Nodes</p>
                  <p className="text-2xl font-bold text-info-900">{data?.kubernetes.total_nodes || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-success-50 rounded-lg p-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-success-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-success-600">Running Pods</p>
                  <p className="text-2xl font-bold text-success-900">{data?.kubernetes.running_pods || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-primary-50 rounded-lg p-4">
              <div className="flex items-center">
                <CircleStackIcon className="h-5 w-5 text-primary-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-primary-600">Total Pods</p>
                  <p className="text-2xl font-bold text-primary-900">{data?.kubernetes.total_pods || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Alerts</h2>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="bg-danger-50 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-danger-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-danger-600">Unread Alerts</p>
                  <p className="text-2xl font-bold text-danger-900">{data?.alerts.unread || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-success-50 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-success-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-success-600">Read Alerts</p>
                  <p className="text-2xl font-bold text-success-900">{data?.alerts.read || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-warning-50 rounded-lg p-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-warning-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-warning-600">Total Alerts</p>
                  <p className="text-2xl font-bold text-warning-900">{data?.alerts.total || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 